import {
    useState, useRef, useCallback, useEffect
} from 'react';

/**
 * Hook to track if scroll position exceeds a threshold
 * Useful for triggering compact/expanded modes based on scroll position
 *
 * @param {number} threshold - Scroll position threshold in pixels (default: 50)
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 100)
 * @returns {Object} Object containing scroll state and handlers
 * @returns {boolean} isScrolled - Whether scroll position exceeds threshold
 * @returns {Function} handleScroll - Scroll event handler
 * @returns {React.RefObject} scrollRef - Ref for the scrollable element
 *
 * @example
 * const { isScrolled, handleScroll, scrollRef } = useScrollThreshold(50, 100);
 *
 * <div ref={scrollRef} onScroll={handleScroll}>
 *   {isScrolled ? 'Compact Mode' : 'Expanded Mode'}
 * </div>
 */
export const useScrollThreshold = (threshold = 50, debounceMs = 50) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const scrollRef = useRef(null);
    const lastScrollTopRef = useRef(0);
    const scrollTimeoutRef = useRef(null);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;

        if (!el) {
            return;
        }

        const currentScrollTop = el.scrollTop;

        // Always update the scroll position ref
        lastScrollTopRef.current = currentScrollTop;

        // Debounce the isScrolled state update to reduce re-renders
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            setIsScrolled(currentScrollTop > threshold);
        }, debounceMs);
    }, [threshold, debounceMs]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    return { isScrolled, handleScroll, scrollRef };
};

export default useScrollThreshold;
